import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Row, Col, Card, Button, Image, Dropdown, Menu, Modal, message, Input, Form, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import CustomerMainLayout from "../CustomerMainLayout";
import DottedLoader from "../../DottedLoader";
import { ReactComponent as ImgOutline } from '../../../../assets/images/black-large-img-outline.svg';
import { ReactComponent as MoreIcon } from '../../../../icons/more.svg';
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import { getBaseURL } from "../../../../environments/env";
import "./PropsLibrary.scss";
import DeleteEntityModal from "../../DeleteEntityModal/DeleteEntityModal";
import EditPropModal from "./EditPropModal";

const CUSTOMER_USERNAME = localStorage.getItem("username");
const COMPANY_ID = localStorage.getItem("company_id");

const PropsLibrary = () => {
    const [images, setImages] = useState([]);
    const [displayedImages, setDisplayedImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [deleteImageLoader, setDeleteImageLoader] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const galleryRef = useRef();
    const pageRef = useRef(1);
    const isDeletingRef = useRef(false);
    const isEditingRef = useRef(false);
    const pageSize = 20;

    // Helper function to generate keywords from image data
    const generateKeywords = useCallback((image) => {
        let keywords = [];
        
        // Add ID as keyword
        if (image.id) {
            keywords.push(String(image.id).toLowerCase());
        }
        
        // Add keywords from name
        if (image.name) {
            const nameKeywords = image.name.replace(/[^a-zA-Z0-9 ]/g, " ").split(" ");
            nameKeywords.forEach(keyword => {
                if (keyword.length > 1 && !keywords.includes(keyword.toLowerCase())) {
                    keywords.push(keyword.toLowerCase());
                }
            });
        }
        
        // Add keywords from category
        if (image.category) {
            const categoryKeywords = image.category.replace(/[^a-zA-Z0-9 ]/g, " ").split(" ");
            categoryKeywords.forEach(keyword => {
                if (keyword.length > 1 && !keywords.includes(keyword.toLowerCase())) {
                    keywords.push(keyword.toLowerCase());
                }
            });
        }
        
        return keywords;
    }, []);

    // Fetch props images from API
    const fetchPropsImages = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                action: 'get_all_props',
                username: CUSTOMER_USERNAME,
                company_id: COMPANY_ID
            };

            const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);

            if (response.data && response.data.result === 'success' && Array.isArray(response.data.data)) {
                const baseURL = getBaseURL();
                const processedImages = response.data.data.map((image, index) => {
                    const baseImageUrl = baseURL + image.image_uri;;
                    const thumbnailUrl = image?.thumbnail ? baseURL + image.thumbnail : null;
                    const imageData = {
                        src: baseImageUrl,
                        width: image.width,
                        height: image.height,
                        depth: image.depth,
                        id: image.id || index,
                        title: `Prop ${image.id || index + 1}`,
                        created_on: image.created_on,
                        image_uri: image.image_uri,
                        ...image
                    };
                    imageData.thumbnail = thumbnailUrl;
                    // Generate keywords array from available fields
                    imageData.keywords = generateKeywords(imageData);

                    return imageData;
                });
                setImages(processedImages);
            } else {
                // Handle empty or unexpected response
                setImages([]);
            }
        } catch (error) {
            console.error("Error fetching props images:", error);
            setImages([]);
        } finally {
            setLoading(false);
        }
    }, [generateKeywords]);

    useEffect(() => {
        fetchPropsImages();
    }, []);

    // Filter images based on search value (search in keywords)
    const filteredImages = useMemo(() => {
        if (!searchValue.trim()) {
            return images;
        }
        const searchLower = searchValue.toLowerCase().trim();
        // Split search query into individual keywords
        const searchKeywords = searchLower.split(/\s+/).filter(k => k.length > 0);
        
        return images.filter(image => {
            // If image has keywords array, search in it
            if (image.keywords && Array.isArray(image.keywords)) {
                // Check if any search keyword matches any keyword in the image (OR logic)
                return searchKeywords.some(searchKeyword => 
                    image.keywords.some(keyword => keyword.includes(searchKeyword))
                );
            }
            // Fallback to name and category if keywords don't exist
            const name = (image.name || '').toLowerCase();
            const category = (image.category || '').toLowerCase();
            return searchKeywords.some(searchKeyword => 
                name.includes(searchKeyword) || category.includes(searchKeyword)
            );
        });
    }, [images, searchValue]);

    const loadMoreImages = useCallback(() => {
        if (!filteredImages || filteredImages.length === 0) return;

        const startIndex = (pageRef.current - 1) * pageSize;
        const newImages = filteredImages.slice(startIndex, startIndex + pageSize);

        if (newImages.length > 0) {
            setDisplayedImages((prev) => [...prev, ...newImages]);
            pageRef.current += 1;
        }
    }, [filteredImages, pageSize]);

    useEffect(() => {
        setDisplayedImages([]);
        pageRef.current = 1;

        if (filteredImages && filteredImages.length > 0) {
            loadMoreImages();
        }
    }, [filteredImages, loadMoreImages]);

    const handleScroll = useCallback(() => {
        if (!galleryRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            loadMoreImages();
        }
    }, [loadMoreImages]);

    const handleImageClick = (index, e) => {
        // Prevent opening if clicking on dropdown or menu items
        if (e && (e.target.closest('.ant-dropdown') || e.target.closest('.ant-menu'))) {
            return;
        }
        // Don't open if delete/edit modal is visible or about to be visible, or if delete/edit was just clicked
        if (deleteModalVisible || itemToDelete || isDeletingRef.current || editModalVisible || itemToEdit || isEditingRef.current) {
            return;
        }
        const imageData = displayedImages[index];
        if (imageData) {
            setSelectedImage(imageData);
            // Use setTimeout to ensure Image component is rendered before showing preview
            setTimeout(() => {
                setIsModalVisible(true);
            }, 0);
        }
    };

    const handleDelete = (item) => {
        isDeletingRef.current = true;
        setItemToDelete(item);
        setDeleteModalVisible(true);
        // Reset the flag after a short delay to allow any pending card clicks to be blocked
        setTimeout(() => {
            isDeletingRef.current = false;
        }, 100);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setDeleteImageLoader(true);

        try {
            const payload = {
                action: 'delete_prop',
                prop_id: itemToDelete.id,
                username: CUSTOMER_USERNAME,
                company_id: COMPANY_ID
            };

            const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);

            if (response.data && response.data.result === 'success') {
                setDeleteImageLoader(false);
                message.success('Prop deleted successfully');
                // Remove from displayed images
                setDisplayedImages(prev => prev.filter(img => img.id !== itemToDelete.id));
                setImages(prev => prev.filter(img => img.id !== itemToDelete.id));
                setDeleteModalVisible(false);
                setItemToDelete(null);
                isDeletingRef.current = false;
            } else {
                message.error('Failed to delete prop');
            }
        } catch (error) {
            console.error("Error deleting prop:", error);
            message.error('Error deleting prop');
        }
    };

    const handleEdit = (item) => {
        isEditingRef.current = true;
        setItemToEdit(item);
        setEditModalVisible(true);
        // Reset the flag after a short delay to allow any pending card clicks to be blocked
        setTimeout(() => {
            isEditingRef.current = false;
        }, 100);
    };

    const handleEditCancel = () => {
        setEditModalVisible(false);
        setItemToEdit(null);
        isEditingRef.current = false;
    };

    const handleEditSuccess = (updatedItem) => {
        // Update the item in the images array and regenerate keywords
        setImages(prev => prev.map(img => {
            if (img.id === updatedItem.id) {
                const updated = { ...updatedItem };
                updated.keywords = generateKeywords(updated);
                return updated;
            }
            return img;
        }));
    };

    const getMenuOptions = (item) => {
        return (
            <Menu>
                <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
                    <span className="manrope f-14">Edit</span>
                </Menu.Item>
                <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => handleDelete(item)}>
                    <span className="manrope f-14">Delete</span>
                </Menu.Item>
            </Menu>
        );
    };



    if (loading) {
        return (
            <CustomerMainLayout selected="34">
                <div>
                    <DottedLoader />
                </div>
            </CustomerMainLayout>
        );
    }

    if (!displayedImages.length && !loading) {
        return (
            <CustomerMainLayout selected="34">
                <div style={{ padding: "20px" }}>
                    <Row className="justify-space-between" style={{ marginBottom: "20px" }}>
                        <Col lg={10} xl={10}>
                            <h1 className="manrope f-24 w-700">Props</h1>
                        </Col>
                        <Col lg={14} xl={14} className="justify-in-end">
                            <div className="search-bar-width mr-8">
                                <Form>
                                    <Input.Group compact size="large" className="d-inline">
                                        <Input
                                            placeholder="Search All Props"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            className="manrope f-14 mat-search-bar"
                                        />
                                    </Input.Group>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                    <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
                        <Col span={24} style={{ textAlign: "center" }}>
                            <ImgOutline />
                            <span className="manrope f-20 mt-24" style={{ display: "block" }}>
                                {searchValue.trim() ? "No props found matching your search" : "No props in your library yet"}
                            </span>
                            {!searchValue.trim() && (
                                <div className="mt-16" style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                                    <Button
                                        type="primary"
                                        className="browse-previews-btn">
                                        Upload Props
                                    </Button>
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </CustomerMainLayout>
        );
    }

    return (
        <CustomerMainLayout selected="34">
            <div style={{ padding: "20px" }}>
                <Row className="justify-space-between" style={{ marginBottom: "20px" }}>
                    <Col lg={10} xl={10}>
                        <h1 className="manrope f-24 w-700">Props</h1>

                    </Col>
                    <Col lg={14} xl={14} className="justify-in-end">
                        {displayedImages.length > 0 && (
                            <div className="search-bar-width mr-8">
                                <Form>
                                    <Input.Group compact size="large" className="d-inline">
                                        <Input
                                            placeholder="Search All Props"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            className="manrope f-14 mat-search-bar"
                                        />
                                    </Input.Group>
                                </Form>
                            </div>
                        )}
                    </Col>
                </Row>

                <div
                    ref={galleryRef}
                    onScroll={handleScroll}
                    style={{ overflowY: "auto", maxHeight: `${window.innerHeight * 0.75}px` }}
                >
                    <Row gutter={[16, 16]}>
                        {displayedImages.map((item, index) => {
                            const formatDimensions = () => {
                                if (!item.width && !item.height && !item.depth) return null;
                                const parts = [];
                                if (item.width) parts.push(`${item.width}"W`);
                                if (item.height) parts.push(`${item.height}"H`);
                                if (item.depth) parts.push(`${item.depth}"D`);
                                return parts.join(' x ');
                            };

                            const dimensions = formatDimensions();

                            return (
                                <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.id || index}>
                                    <Tooltip title={item.name || 'Untitled'}>

                                        <Card
                                            onClick={(e) => handleImageClick(index, e)}
                                            className="prop-card"
                                            bodyStyle={{ padding: 0 }}
                                        >
                                            <div className="prop-card-wrapper">
                                                <Dropdown
                                                    overlay={getMenuOptions(item)}
                                                    trigger={['click']}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="prop-more-icon" onClick={(e) => e.stopPropagation()}>
                                                        <MoreIcon />
                                                    </div>
                                                </Dropdown>
                                                <img
                                                    src={item.thumbnail || item.src}
                                                    alt={item.title || "Prop image"}
                                                    className="prop-img"
                                                />
                                                {item.category && 
                                                <div className="prop-dim-info">
                                                    <div className="prop-dimensions">
                                                        {item.category}
                                                    </div>
                                                </div>}

                                                <div className="prop-identifier-overlay justify-space-between" style={{ gap: '10px' }}>
                                                    <span className="prop-identifier-text">{item.name || 'Untitled'}</span>
                                                    {dimensions && <span className="prop-dimensions" style={{ textWrap: 'nowrap' }}>{dimensions}</span>}
                                                </div>
                                            </div>
                                        </Card>
                                    </Tooltip>
                                </Col>
                            )
                        })}
                    </Row>
                </div>

                {selectedImage && (
                    <Image
                        style={{ display: 'none' }}
                        src={selectedImage.src}
                        preview={{
                            visible: isModalVisible,
                            onVisibleChange: (visible) => {
                                setIsModalVisible(visible);
                                if (!visible) {
                                    setSelectedImage(null);
                                }
                            },
                            getContainer: () => document.body
                        }}
                    />
                )}

                {/* Delete Confirmation Modal */}
                <Modal
                    title="Delete Prop"
                    open={deleteModalVisible}
                    onOk={confirmDelete}
                    onCancel={() => {
                        setDeleteModalVisible(false);
                        setItemToDelete(null);
                        isDeletingRef.current = false;
                    }}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                >
                    <p className="manrope f-14">Are you sure you want to delete this prop? This action cannot be undone.</p>
                </Modal>

                <DeleteEntityModal
                    onCancel={() => {
                        setDeleteModalVisible(false);
                        setItemToDelete(null);
                        isDeletingRef.current = false;
                    }}
                    visible={deleteModalVisible}
                    deletingModelLoader={deleteImageLoader}
                    selected_id={itemToDelete?.id}
                    delete_model={confirmDelete}
                    heading={'Delete prop'}
                    subText={'Are you sure you want to delete this prop image?'}
                />

                {/* Edit Modal */}
                <EditPropModal
                    visible={editModalVisible}
                    onCancel={handleEditCancel}
                    onSuccess={handleEditSuccess}
                    itemToEdit={itemToEdit}
                />
            </div>
        </CustomerMainLayout>
    );
};

export default PropsLibrary;
